import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { useSEO } from "@/hooks/useSEO";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Camera, Save, MapPin, X, Loader2, Shield, Bell, Mail, Trash2, Images, User, SlidersHorizontal } from "lucide-react";
import PortfolioUpload from "@/components/PortfolioUpload";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useArtisanProfile } from "@/hooks/useArtisanProfile";
import { useSearchParams } from "react-router-dom";
import { toast } from "@/hooks/use-toast";
import { useWilayas, useCommunesByWilaya } from "@/hooks/useAlgeriaData";
import { useLanguage } from "@/hooks/useLanguage";
import { useLocalizedNavigate } from "@/hooks/useLocalizedNavigate";
import ArtisanBottomNav from "@/components/ArtisanBottomNav";
import { ARTISAN_SPECIALITES } from "@/lib/constants";
import HandymanSpecialtiesSelector from "@/components/HandymanSpecialtiesSelector";
import { useHandymanSpecialties, useUpsertHandymanSpecialties } from "@/hooks/useHandymanSpecialties";
import type { HandymanSpecialtySelection, HandymanSpecialtyCode } from "@/lib/handymanConfig";

// Skip the "tous" sentinel (index 0) which is a UI-only filter value, not a DB value.
const specialites = ARTISAN_SPECIALITES.slice(1);

const MonProfilArtisan = () => {
  useSEO({ noindex: true });
  const { locale, t } = useLanguage("artisan");
  const lNavigate = useLocalizedNavigate();
  const wilayasData = useWilayas(locale);
  const { user, signOut } = useAuth();
  const [searchParams] = useSearchParams();
  // Permet le deep-link vers un onglet : /mon-profil-artisan?tab=portfolio
  const initialTab = ["profil", "portfolio", "parametres"].includes(searchParams.get("tab") ?? "")
    ? searchParams.get("tab")!
    : "profil";
  const { data: profile, isLoading } = useArtisanProfile();
  const [saving, setSaving] = useState(false);
  const [handymanSpecialties, setHandymanSpecialties] = useState<HandymanSpecialtySelection[]>([]);
  const { data: existingHandymanSpecialties } = useHandymanSpecialties(profile?.id);
  const upsertHandymanSpecialties = useUpsertHandymanSpecialties(profile?.id);

  // Sécurité & Compte
  const [resetSent, setResetSent] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [notifMessages, setNotifMessages] = useState(true);
  const [notifRdv, setNotifRdv] = useState(true);
  const [notifAnnonces, setNotifAnnonces] = useState(true);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);

  // Form state
  const [specialite, setSpecialite] = useState("");
  const [wilaya, setWilaya] = useState("");
  const [commune, setCommune] = useState("");
  const [communesCouvertes, setCommunesCouvertes] = useState<string[]>([]);
  const [phone, setPhone] = useState("");
  const [description, setDescription] = useState("");
  const [raisonSociale, setRaisonSociale] = useState("");
  const [numeroRC, setNumeroRC] = useState("");
  const [adresse, setAdresse] = useState("");
  const [emailEntreprise, setEmailEntreprise] = useState("");
  const [facebookUrl, setFacebookUrl] = useState("");
  const [instagramUrl, setInstagramUrl] = useState("");
  const [websiteUrl, setWebsiteUrl] = useState("");

  // Always FR names for value/DB; localized labels for display
  const communeOptions = useCommunesByWilaya(wilaya, locale);
  const getComLabel = (frName: string) =>
    communeOptions.find((c) => c.city_name === frName)?.label ?? frName;

  useEffect(() => {
    if (profile) {
      setSpecialite(profile.specialite || "");
      setWilaya(profile.wilaya || "");
      setCommune(profile.commune || "");
      setCommunesCouvertes(profile.communes_couvertes || []);
      setPhone(profile.phone || "");
      setDescription(profile.description || "");
      setRaisonSociale(profile.raison_sociale || "");
      setNumeroRC(profile.numero_rc || "");
      setAdresse(profile.adresse || "");
      setEmailEntreprise(profile.email_entreprise || "");
      setFacebookUrl(profile.facebook_url || "");
      setInstagramUrl(profile.instagram_url || "");
      setWebsiteUrl(profile.website_url || "");
      if (profile.logo_url) setLogoPreview(profile.logo_url);
      setNotifMessages(profile.notif_email_messages ?? true);
      setNotifRdv(profile.notif_email_rdv ?? true);
      setNotifAnnonces(profile.notif_email_annonces ?? true);
    }
  }, [profile]);

  // Initialiser les sous-spécialités depuis la BDD
  useEffect(() => {
    if (existingHandymanSpecialties && existingHandymanSpecialties.length > 0) {
      setHandymanSpecialties(
        existingHandymanSpecialties.map((s) => ({
          specialty_code: s.specialty_code as HandymanSpecialtyCode,
          other_specialty_label: s.other_specialty_label ?? undefined,
        }))
      );
    }
  }, [existingHandymanSpecialties]);

  const handleSendResetEmail = async () => {
    if (!user?.email) return;
    const { error } = await supabase.auth.resetPasswordForEmail(user.email, {
      redirectTo: `${window.location.origin}/parametres`,
    });
    if (error) {
      toast({ title: t("profil_edit.toast_error"), description: error.message, variant: "destructive" });
    } else {
      setResetSent(true);
      toast({ title: t("profil_edit.toast_email_sent"), description: t("profil_edit.toast_email_sent_desc") });
      setTimeout(() => setResetSent(false), 60000);
    }
  };

  const handleNotifChange = async (field: "notif_email_messages" | "notif_email_rdv" | "notif_email_annonces", value: boolean) => {
    if (field === "notif_email_messages") setNotifMessages(value);
    if (field === "notif_email_rdv") setNotifRdv(value);
    if (field === "notif_email_annonces") setNotifAnnonces(value);
    if (!user) return;
    const { error } = await supabase
      .from("artisan_profiles")
      .update({ [field]: value })
      .eq("user_id", user.id);
    if (error) toast({ title: t("profil_edit.toast_error"), description: error.message, variant: "destructive" });
  };

  const handleDeleteAccount = async () => {
    if (!user || deleteConfirmText !== "SUPPRIMER") return;
    setDeleting(true);
    try {
      const { error } = await supabase.rpc("delete_own_account" as any);
      if (error) throw error;
      await signOut();
      lNavigate("/");
      toast({ title: t("profil_edit.toast_account_deleted"), description: t("profil_edit.toast_account_deleted_desc") });
    } catch (err: any) {
      toast({ title: t("profil_edit.toast_error"), description: err.message, variant: "destructive" });
    } finally {
      setDeleting(false);
    }
  };

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setLogoPreview(reader.result as string);
      reader.readAsDataURL(file);
    }
  };

  const removeCommune = (c: string) => {
    setCommunesCouvertes(communesCouvertes.filter((x) => x !== c));
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !profile) {
      toast({ title: t("profil_edit.toast_session_expired"), description: t("profil_edit.toast_session_expired_desc"), variant: "destructive" });
      lNavigate("/connexion");
      return;
    }
    setSaving(true);

    try {
      let logoUrl = profile.logo_url;

      if (logoFile) {
        const ext = logoFile.name.split(".").pop();
        const path = `${user.id}/logo.${ext}`;
        const { error: uploadError } = await supabase.storage
          .from("logos")
          .upload(path, logoFile, { upsert: true });
        if (uploadError) throw uploadError;
        const { data: urlData } = supabase.storage.from("logos").getPublicUrl(path);
        logoUrl = urlData.publicUrl;
      }

      const { error } = await supabase
        .from("artisan_profiles")
        .update({
          specialite,
          wilaya,
          commune,
          communes_couvertes: communesCouvertes,
          phone: phone || null,
          description: description || null,
          raison_sociale: raisonSociale || null,
          numero_rc: numeroRC || null,
          adresse: adresse || null,
          logo_url: logoUrl,
          email_entreprise: emailEntreprise || null,
          facebook_url: facebookUrl || null,
          instagram_url: instagramUrl || null,
          website_url: websiteUrl || null,
        })
        .eq("user_id", user.id);

      if (error) throw error;

      // Sauvegarder les sous-spécialités si artisan polyvalent
      if (specialite === "Artisan polyvalent") {
        await upsertHandymanSpecialties.mutateAsync(handymanSpecialties);
      }

      toast({ title: t("profil_edit.toast_saved"), description: t("profil_edit.toast_saved_desc") });
    } catch (error: any) {
      toast({ title: t("profil_edit.toast_error"), description: error.message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen">
        <Navbar />
        <main className="container flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </main>
        <Footer />
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen">
        <Navbar />
        <main className="container max-w-2xl py-8 text-center">
          <p className="text-muted-foreground">{t("profil_edit.no_profile")}</p>
          <Button variant="hero" className="mt-4" onClick={() => lNavigate("/inscription")}>
            {t("profil_edit.create_profile_cta")}
          </Button>
        </main>
        <Footer />
      </div>
    );
  }

  const isEntreprise = profile.type === "entreprise";

  return (
    <div className="min-h-screen">
      <Navbar />
      <main className="container max-w-2xl py-8 pb-24">

        {/* En-tête compact avec avatar intégré */}
        <div className="mb-6 flex items-center gap-4">
          <label htmlFor="logo-edit" className="relative cursor-pointer shrink-0 group">
            <div className="relative h-14 w-14 overflow-hidden rounded-full border-2 border-primary/20 bg-primary/5 flex items-center justify-center transition-colors group-hover:border-primary/40">
              {logoPreview ? (
                <>
                  <img src={logoPreview} alt="Logo" className="h-full w-full object-cover" />
                  <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <Camera className="h-4 w-4 text-white" />
                  </div>
                </>
              ) : (
                <Camera className="h-6 w-6 text-primary/40 group-hover:text-primary/70 transition-colors" />
              )}
            </div>
          </label>
          <input id="logo-edit" type="file" accept="image/*" className="hidden" onChange={handleLogoChange} />
          <div>
            <h1 className="font-display text-2xl font-bold">{t("profil_edit.title")}</h1>
            <p className="text-sm text-muted-foreground">{t("profil_edit.subtitle")}</p>
          </div>
        </div>

        <Tabs defaultValue={initialTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="profil" className="gap-1.5">
              <User className="h-3.5 w-3.5" />
              {t("profil_edit.tab_profil")}
            </TabsTrigger>
            <TabsTrigger value="portfolio" className="gap-1.5">
              <Images className="h-3.5 w-3.5" />
              {t("profil_edit.tab_portfolio")}
            </TabsTrigger>
            <TabsTrigger value="parametres" className="gap-1.5">
              <SlidersHorizontal className="h-3.5 w-3.5" />
              {t("profil_edit.tab_params")}
            </TabsTrigger>
          </TabsList>

          {/* ─── Onglet Profil ─── */}
          <TabsContent value="profil">
            <form onSubmit={handleSave} className="space-y-5">

              {/* Infos principales */}
              <Card>
                <CardHeader><CardTitle>{t("profil_edit.card_main")}</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                  {isEntreprise && (
                    <div className="grid gap-4 sm:grid-cols-2">
                      <div className="space-y-2">
                        <Label className="font-display font-semibold">{t("profil_edit.label_raison_sociale")}</Label>
                        <Input value={raisonSociale} onChange={(e) => setRaisonSociale(e.target.value)} />
                      </div>
                      <div className="space-y-2">
                        <Label className="font-display font-semibold">{t("profil_edit.label_rc")}</Label>
                        <Input value={numeroRC} onChange={(e) => setNumeroRC(e.target.value)} />
                      </div>
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label className="font-display font-semibold">{t("profil_edit.label_specialite")} *</Label>
                    <Select value={specialite} onValueChange={(v) => { setSpecialite(v); if (v !== "Artisan polyvalent") setHandymanSpecialties([]); }}>
                      <SelectTrigger><SelectValue placeholder={t("profil_edit.placeholder_select")} /></SelectTrigger>
                      <SelectContent>
                        {specialites.map((s) => <SelectItem key={s.value} value={s.value}>{locale === "ar" ? s.label_ar : s.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>

                  {specialite === "Artisan polyvalent" && (
                    <div className="space-y-2">
                      <Label className="font-display font-semibold">{t("profil_edit.label_sous_specialites")}</Label>
                      <HandymanSpecialtiesSelector
                        value={handymanSpecialties}
                        onChange={setHandymanSpecialties}
                      />
                    </div>
                  )}

                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label className="font-display font-semibold">{t("profil_edit.label_phone")}</Label>
                      <Input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="0555 12 34 56" />
                    </div>
                    {isEntreprise && (
                      <div className="space-y-2">
                        <Label className="font-display font-semibold">{t("profil_edit.label_email_entreprise")}</Label>
                        <Input type="email" value={emailEntreprise} onChange={(e) => setEmailEntreprise(e.target.value)} />
                      </div>
                    )}
                  </div>

                  {isEntreprise && (
                    <div className="space-y-2">
                      <Label className="font-display font-semibold">{t("profil_edit.label_adresse")}</Label>
                      <Input value={adresse} onChange={(e) => setAdresse(e.target.value)} placeholder={t("profil_edit.placeholder_adresse")} />
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label className="font-display font-semibold">{t("profil_edit.label_description")}</Label>
                    <Textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder={t("profil_edit.placeholder_description")} className="min-h-[100px]" />
                  </div>
                </CardContent>
              </Card>

              {/* Zone d'intervention */}
              <Card>
                <CardHeader><CardTitle>{t("profil_edit.card_zone")}</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label className="font-display font-semibold">{t("profil_edit.label_wilaya")} *</Label>
                      <Select value={wilaya} onValueChange={setWilaya}>
                        <SelectTrigger><SelectValue placeholder={t("profil_edit.label_wilaya")} /></SelectTrigger>
                        <SelectContent>
                          {wilayasData.map((w) => (
                            <SelectItem key={w.state_name} value={w.state_name}>{w.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="font-display font-semibold">{t("profil_edit.label_commune")}</Label>
                      <Select key={wilaya} value={commune} onValueChange={setCommune} disabled={!wilaya}>
                        <SelectTrigger>
                          <SelectValue placeholder={wilaya ? t("profil_edit.placeholder_commune") : t("profil_edit.placeholder_wilaya_first")} />
                        </SelectTrigger>
                        <SelectContent>
                          {communeOptions.map((c) => (
                            <SelectItem key={c.city_name} value={c.city_name}>{c.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="font-display font-semibold">{t("profil_edit.label_communes_couvertes")}</Label>
                    <Select
                      value=""
                      onValueChange={(val) => {
                        if (!communesCouvertes.includes(val)) {
                          setCommunesCouvertes([...communesCouvertes, val]);
                        }
                      }}
                      disabled={!wilaya}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={wilaya ? t("profil_edit.placeholder_add_commune") : t("profil_edit.placeholder_wilaya_first")} />
                      </SelectTrigger>
                      <SelectContent>
                        {communeOptions
                          .filter((c) => !communesCouvertes.includes(c.city_name))
                          .map((c) => (
                            <SelectItem key={c.city_name} value={c.city_name}>{c.label}</SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                    {communesCouvertes.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {communesCouvertes.map((c) => (
                          <Badge key={c} variant="secondary" className="gap-1 pr-1">
                            <MapPin className="h-3 w-3" />{getComLabel(c)}
                            <button type="button" onClick={() => removeCommune(c)} className="ms-1 rounded-full p-0.5 hover:bg-muted">
                              <X className="h-3 w-3" />
                            </button>
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Réseaux sociaux (entreprise uniquement) */}
              {isEntreprise && (
                <Card>
                  <CardHeader><CardTitle>{t("profil_edit.card_social")}</CardTitle></CardHeader>
                  <CardContent className="space-y-3">
                    <div className="space-y-2">
                      <Label className="font-display font-semibold">Facebook</Label>
                      <Input value={facebookUrl} onChange={(e) => setFacebookUrl(e.target.value)} placeholder="https://facebook.com/..." />
                    </div>
                    <div className="space-y-2">
                      <Label className="font-display font-semibold">Instagram</Label>
                      <Input value={instagramUrl} onChange={(e) => setInstagramUrl(e.target.value)} placeholder="https://instagram.com/..." />
                    </div>
                    <div className="space-y-2">
                      <Label className="font-display font-semibold">Site web</Label>
                      <Input value={websiteUrl} onChange={(e) => setWebsiteUrl(e.target.value)} placeholder="https://..." />
                    </div>
                  </CardContent>
                </Card>
              )}

              <Button variant="hero" size="xl" className="w-full" type="submit" disabled={saving}>
                {saving ? <Loader2 className="me-2 h-5 w-5 animate-spin" /> : <Save className="me-2 h-5 w-5" />}
                {saving ? t("profil_edit.saving") : t("profil_edit.save")}
              </Button>
            </form>
          </TabsContent>

          {/* ─── Onglet Portfolio ─── */}
          <TabsContent value="portfolio">
            <PortfolioUpload />
          </TabsContent>

          {/* ─── Onglet Paramètres ─── */}
          <TabsContent value="parametres" className="space-y-5">

            {/* Alertes email */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Bell className="h-4 w-4 text-primary" />
                  {t("profil_edit.card_notifs")}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium">{t("profil_edit.notif_annonces_title")}</p>
                    <p className="text-xs text-muted-foreground">{t("profil_edit.notif_annonces_desc")}</p>
                  </div>
                  <Switch
                    checked={notifAnnonces}
                    onCheckedChange={(v) => handleNotifChange("notif_email_annonces", v)}
                  />
                </div>
                <Separator />
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium">{t("profil_edit.notif_messages_title")}</p>
                    <p className="text-xs text-muted-foreground">{t("profil_edit.notif_messages_desc")}</p>
                  </div>
                  <Switch
                    checked={notifMessages}
                    onCheckedChange={(v) => handleNotifChange("notif_email_messages", v)}
                  />
                </div>
                <Separator />
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium">{t("profil_edit.notif_rdv_title")}</p>
                    <p className="text-xs text-muted-foreground">{t("profil_edit.notif_rdv_desc")}</p>
                  </div>
                  <Switch
                    checked={notifRdv}
                    onCheckedChange={(v) => handleNotifChange("notif_email_rdv", v)}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Sécurité */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="h-4 w-4 text-primary" />
                  {t("profil_edit.card_security")}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <p className="text-sm font-medium mb-0.5">{t("profil_edit.pwd_title")}</p>
                  <p className="text-xs text-muted-foreground mb-3">
                    {t("profil_edit.pwd_desc")}
                  </p>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleSendResetEmail}
                    disabled={resetSent}
                    className="gap-2"
                  >
                    <Mail className="h-4 w-4" />
                    {resetSent ? t("profil_edit.pwd_sent") : t("profil_edit.pwd_send")}
                  </Button>
                </div>

                <Separator />

                <div>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <button
                        type="button"
                        className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-destructive transition-colors"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                        {t("profil_edit.delete_link")}
                      </button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>{t("profil_edit.delete_dialog_title")}</AlertDialogTitle>
                        <AlertDialogDescription>
                          {t("profil_edit.delete_dialog_desc")}
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <div className="py-3 space-y-2">
                        <Label className="text-sm">
                          {t("profil_edit.delete_confirm_label")}
                        </Label>
                        <Input
                          value={deleteConfirmText}
                          onChange={(e) => setDeleteConfirmText(e.target.value)}
                          placeholder="SUPPRIMER"
                          className="font-mono"
                        />
                      </div>
                      <AlertDialogFooter>
                        <AlertDialogCancel onClick={() => setDeleteConfirmText("")}>
                          {t("profil_edit.delete_cancel")}
                        </AlertDialogCancel>
                        <AlertDialogAction
                          className="bg-destructive hover:bg-destructive/90"
                          disabled={deleteConfirmText !== "SUPPRIMER" || deleting}
                          onClick={handleDeleteAccount}
                        >
                          {deleting ? t("profil_edit.delete_deleting") : t("profil_edit.delete_confirm_btn")}
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </CardContent>
            </Card>

          </TabsContent>
        </Tabs>
      </main>
      <Footer />
      <ArtisanBottomNav />
    </div>
  );
};

export default MonProfilArtisan;
